
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, CheckCircle, ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Header } from '@/components/landing/Header';
import { StudiesSidebar } from '@/components/studies/StudiesSidebar';
import { StudyContent } from '@/components/studies/StudyContent';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';

// Define the initial study content
const marchMadnessStudy = {
  id: 'march-madness-2023',
  title: 'When AI Meets March Madness',
  date: 'March 2023',
  subtitle: 'How Tournament Upsets Mirror Market Volatility - And What Traders Can Learn',
  content: (
    <>
      <p className="italic text-gray-300 mb-6">
        Welcome to the inaugural edition of Stewie Insights, where we explore the fascinating intersections between artificial
        intelligence, data patterns, and market behavior. Today, we're diving into our recent internal study applying our AI
        trading algorithms to an unexpected domain: March Madness basketball.
      </p>

      <h2 className="text-xl font-bold text-blue-400">Beyond Brackets: The Pattern Recognition Connection</h2>
      <p>
        When I first proposed analyzing March Madness through the lens of our trading algorithms, skepticism was certainly
        present. What could college basketball possibly teach us about market behavior? As it turns out, quite a lot.
      </p>
      <p>
        Our AI models excel at one fundamental task: identifying patterns in seemingly chaotic data. Whether those patterns
        emerge from stock price movements or from the frenetic pace of tournament basketball, the underlying analytical
        principles remain remarkably similar.
      </p>
      <p>
        Pattern recognition doesn't care about the domain. Our models look for statistical signatures – deviations from
        expected behavior that might indicate an opportunity or risk. These signatures appear in financial markets, but also in
        competitive tournaments where human psychology, performance pressure, and systematic biases all come into play.
      </p>

      <h2 className="text-xl font-bold text-blue-400">The Cinderella Effect: Identifying Undervalued Assets</h2>
      <p>
        Perhaps the most compelling parallel we discovered involves tournament upsets and undervalued market assets. Our
        AI identified several key indicators that predicted when lower-seeded teams would outperform expectations:
      </p>
      <ol className="list-decimal pl-5 space-y-2">
        <li>
          <span className="font-semibold">Performance momentum diverging from public perception</span> – Teams that were improving rapidly but hadn't yet
          attracted widespread attention
        </li>
        <li>
          <span className="font-semibold">Statistical anomalies in specific performance metrics</span> – Exceptional performance in areas that traditional
          analysis underweights
        </li>
        <li>
          <span className="font-semibold">Contextual advantages</span> – Matchup-specific factors that create unexpected edges
        </li>
      </ol>
      <p>
        These same patterns frequently emerge in undervalued assets in financial markets. The stock that's improving
        fundamentals haven't yet been recognized by the broader market. The company with exceptional performance in
        metrics that traditional valuation models don't adequately capture. The business with specific advantages against
        current market conditions that others have overlooked.
      </p>

      <h2 className="text-xl font-bold text-blue-400">Statistical Dominance vs. Adaptive Performance</h2>
      <p>
        Our most intriguing finding came from comparing statistically dominant teams with those showing adaptive
        performance characteristics. Historically, March Madness brackets heavily favor teams with impressive season-long
        statistics – the equivalent of investing based purely on trailing indicators.
      </p>
      <p>
        However, our AI revealed something different. Teams demonstrating adaptive performance – the ability to adjust
        strategies based on opponents and game conditions – outperformed statistically dominant teams in high-pressure
        tournament scenarios by a significant margin.
      </p>
      <p>
        This mirrors what we've observed in market behavior during volatility spikes. Companies with adaptive business
        models often outperform those with historically dominant but rigid financial profiles during periods of economic
        uncertainty.
      </p>

      <h2 className="text-xl font-bold text-blue-400">Practical Applications for Your Trading Strategy</h2>
      <p>
        First, thank you for reading this far but let's keep pushing. How can these insights enhance your approach to the
        markets? Our analysis suggests three key principles:
      </p>
      <ol className="list-decimal pl-5 space-y-2">
        <li>
          <span className="font-semibold">Look Beyond Traditional Metrics</span><br />
          Just as focusing solely on a team's season scoring average misses crucial performance factors, relying exclusively
          on traditional financial metrics provides an incomplete picture. Consider incorporating alternative data points that
          measure adaptability and resilience.
        </li>
        <li>
          <span className="font-semibold">Identify Divergence Between Momentum and Perception</span><br />
          The sweet spot for opportunity often lies where performance momentum is accelerating but public perception hasn't
          caught up. Our models identified this gap as the single strongest predictor of both tournament upsets and market
          outperformance.
        </li>
        <li>
          <span className="font-semibold">Context Matters More Than Categories</span><br />
          Basketball analysts often make predictions based on team categories ("defensive powerhouse," "three-point
          specialists"). Similarly, market analysts categorize stocks in ways that can obscure contextual advantages. Our AI
          suggests that specific matchup dynamics often override categorical strengths and weaknesses.
        </li>
      </ol>

      <h2 className="text-xl font-bold text-blue-400">What's Next: From Brackets to Portfolios</h2>
      <p>
        As we push through our March Madness experiment, we're beyond excited to closely watch our algorithms in action.
        By expanding our pattern recognition capabilities beyond traditional financial data, we're fine tuning our ability to
        identify opportunities others might miss.
      </p>
      <p>
        In our next newsletter, we'll explore how these same principles can be applied to portfolio diversification strategies,
        drawing parallels between tournament bracket construction and risk-optimized investment allocations.
      </p>
      <p>
        Until then, remember that patterns exist everywhere – sometimes the most valuable insights come from unexpected
        places.
      </p>
      <p className="italic text-gray-400">
        Have questions about our AI methodology or suggestions for future analyses? Contact our research team at
        team@stewietrader.com
      </p>

      <div className="mt-8 mb-4 text-center">
        <h3 className="uppercase text-sm font-bold tracking-wider text-blue-400">STEWIE INSIGHTS</h3>
        <p className="text-gray-400 text-sm">
          is the official newsletter of Stewie Trader's AI Trading Platform. Published monthly, it provides unique perspectives at
          the intersection of artificial intelligence and financial markets.
        </p>
      </div>
    </>
  )
};

// Create a studies array (with just our one study for now)
const studiesData = [marchMadnessStudy];

const Studies = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [activeStudyId, setActiveStudyId] = useState(marchMadnessStudy.id);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    
    try {
      const { error } = await supabase
        .from('newsletter_subscriptions')
        .insert([{ email }]);
      
      if (error) {
        if (error.code === '23505') {
          toast({
            title: "Already subscribed",
            description: "This email is already on our newsletter list.",
            variant: "default",
          });
        } else {
          throw error;
        }
      } else {
        setIsSubscribed(true);
        setEmail('');
        toast({
          title: "Successfully subscribed!",
          description: "You'll receive our next Stewie Insights newsletter.",
        });
      }
    } catch (error) {
      console.error('Error subscribing to newsletter:', error);
      toast({
        title: "Subscription failed",
        description: "There was an error processing your subscription. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get the active study based on activeStudyId
  const activeStudy = studiesData.find(study => study.id === activeStudyId) || marchMadnessStudy;

  return (
    <div className="flex flex-col min-h-screen w-full overflow-x-hidden bg-gray-900">
      <Header />
      <div className="flex-grow flex">
        <SidebarProvider defaultOpen={false}>
          <StudiesSidebar 
            studies={studiesData} 
            activeStudyId={activeStudyId} 
            onSelectStudy={setActiveStudyId} 
          />
          <SidebarInset className="pt-16 pb-16 px-4 sm:px-6 lg:px-8">
            <div className="max-w-5xl mx-auto">
              <div className="flex items-center mb-2 mt-6">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="text-white hover:text-blue-300 mr-2" 
                  onClick={() => navigate('/')}
                  aria-label="Back to home"
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
              </div>
              
              <h1 className="text-2xl md:text-3xl text-white font-bold mb-6">Stewie Insights</h1>

              <StudyContent 
                id={activeStudy.id}
                title={activeStudy.title}
                subtitle={activeStudy.subtitle}
                content={activeStudy.content}
              />

              {/* Newsletter Signup Form */}
              <Card className="bg-slate-800 border-slate-700 text-white overflow-hidden">
                <CardHeader className="border-b border-slate-700 bg-gradient-to-r from-blue-800 to-violet-800">
                  <div className="flex items-center space-x-2">
                    <Mail className="h-5 w-5 text-blue-200" />
                    <CardTitle>Subscribe to Stewie Insights</CardTitle>
                  </div>
                  <CardDescription className="text-gray-200">
                    Get monthly AI trading insights delivered straight to your inbox
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  {!isSubscribed ? (
                    <form onSubmit={handleSubscribe} className="space-y-4">
                      <div className="grid w-full items-center gap-2">
                        <Label htmlFor="email" className="text-white">Email</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="you@example.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="bg-slate-700 border-slate-600 text-white"
                          required
                        />
                      </div>
                      
                      <Button 
                        type="submit"
                        className="w-full bg-blue-600 hover:bg-blue-700"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Subscribing..." : "Subscribe"}
                      </Button>
                    </form>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-4 text-center">
                      <CheckCircle className="h-12 w-12 text-green-500 mb-2" />
                      <h3 className="font-bold text-xl">Thanks for subscribing!</h3>
                      <p className="text-gray-300 mt-1">You'll receive our next issue directly to your inbox.</p>
                    </div>
                  )}
                </CardContent>
                <CardFooter className="border-t border-slate-700 text-sm text-gray-400">
                  We respect your privacy. Unsubscribe anytime.
                </CardFooter>
              </Card>
            </div>
          </SidebarInset>
        </SidebarProvider>
      </div>
    </div>
  );
};

export default Studies;
